// Eskom PARCC — the Lumen shell.
//
// Sidebar + column (header, then the routed screen). It owns the transient
// chrome state (sidebar collapse, palette, account/password modals) and the
// global `/` and Escape handling the handoff specifies. The routed screens
// themselves are the SAME components Legacy renders — only this frame differs.
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AccountModal } from '../../auth/AccountModal';
import { useUiPrefs } from '../../../lib/uiPrefs';
import { LumenSidebar } from './LumenSidebar';
import { LumenHeader } from './LumenHeader';
import { CommandPalette } from './CommandPalette';
import { mainClass } from '../routes';

/** True when the keystroke landed in a field, where `/` is a literal slash. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
}

export function LumenShell(): React.JSX.Element {
  const { sidebarDefault } = useUiPrefs();
  const [collapsed, setCollapsed] = useState(sidebarDefault === 'collapsed');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const location = useLocation();

  // Follow the saved default when it changes in Settings, without fighting a
  // manual collapse during the same session.
  useEffect(() => {
    setCollapsed(sidebarDefault === 'collapsed');
  }, [sidebarDefault]);

  // Navigating clears every transient overlay (handoff §Interactions).
  useEffect(() => {
    setPaletteOpen(false);
  }, [location.pathname]);

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === '/' && !isTypingTarget(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setPaletteOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  return (
    <div className="lumen-shell">
      <LumenSidebar collapsed={collapsed} onOpenAccount={() => setAccountOpen(true)} />
      <div className="lumen-body">
        <LumenHeader
          onToggleSidebar={() => setCollapsed((c) => !c)}
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenAccount={() => setAccountOpen(true)}
        />
        {/* Full-bleed screens (the AI workspace) drop the page padding. Same
            route-table flag Legacy reads, so the two shells cannot disagree. */}
        <main className={mainClass(location.pathname)}>
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
}
